import { useEffect, useState } from 'react';
import styled from 'styled-components';

import curiLogo from '../../../assets/images/app/curiLogo.svg';
import loadingStars from '../../../assets/images/app/loaderstars.svg';
import theme from '../../../assets/theme';
import { LoadingDots } from '../../_atoms/DismissableQuestionList/DismissableQuestionList';

const steps = [
  'Detecting the pedagogy, topic and learning objective for your lesson',
  'Always review content generated by AI',
  'Adding interactive activities',
  'Creating a PIN for your students to join',
  'Putting it together as an interactive lesson',
  'Pro tip: Click Generate Lesson to see your recent prompts and refine your lesson',
  'Personalizing the lesson for you',
];

const AnimatedProgressSteps = ({
  isSmallTemplates,
  darkMode = false,
}: {
  isSmallTemplates?: boolean;
  darkMode?: boolean;
}) => {
  const [currentProgressStepIndex, setCurrentProgressStepIndex] = useState<number>(0);

  useEffect(() => {
    if (currentProgressStepIndex < steps.length) {
      const timer = setInterval(
        () => setCurrentProgressStepIndex(currentProgressStepIndex + 1),
        5000,
      );
      return () => clearInterval(timer);
    } else {
      setCurrentProgressStepIndex(steps.length - 1);
    }
  }, [currentProgressStepIndex]);

  return (
    <AnimatedTipsContainer>
      <MagicPulsingLoader />
      <div style={{ display: 'flex', width: '100%' }}>
        {steps &&
          steps.map((step, i) => (
            <StyledTip
              $isVisible={i === currentProgressStepIndex}
              key={i}
              $isSmallTemplates={isSmallTemplates}
              style={{
                color: darkMode ? theme.colors.white : theme.colors.greyDark,
                opacity: i === currentProgressStepIndex ? 1 : 0,
              }}
            >
              <h1>
                {step} <LoadingDots>...</LoadingDots>
              </h1>
            </StyledTip>
          ))}
      </div>
    </AnimatedTipsContainer>
  );
};

const ShowLessonCreationProgressBar = ({
  isSmallTemplates,
  darkMode = false,
}: {
  isSmallTemplates?: boolean;
  darkMode?: boolean;
}) => {
  return (
    <AnimatedProgressSteps darkMode={darkMode} isSmallTemplates={isSmallTemplates} />
  );
};

export default ShowLessonCreationProgressBar;

const AnimatedTipsContainer = styled.div`
  margin-top: 100px;
  height: 50px;
  position: relative;
  width: 90%;
  margin-bottom: 150px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;

  @media (max-width: 450px) {
    width: 90%;
    margin-top: 120px;
  }
`;

const MagicPulsingLoader = () => {
  return <StlyedPulse />;
};

const StlyedPulse = styled.div`
  width: 40px;
  height: 40px;
  aspect-ratio: 1;
  border-radius: 50%;
  box-shadow: 0 0 0 0 ${theme.colors.translucentOrange};
  animation: l2 1.5s infinite linear;
  position: relative;
  background: center / contain no-repeat url(${curiLogo.src});

  &:before,
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: 0 0 0 0 ${theme.colors.translucentOrange};
    animation: inherit;
    animation-delay: -0.5s;
  }
  &::after {
    content: '';
    position: absolute;
    inset: -20px;
    border-radius: inherit;
    background: url(${loadingStars.src}) no-repeat;
    background-size: 100% 100%;
    animation: pulse-stars 1.5s infinite linear;
    opacity: 0;
  }
  &:after {
    animation-delay: -1s;
  }
  @keyframes l2 {
    100% {
      box-shadow: 0 0 0 40px rgba(255, 255, 255, 0.1); //white
    }
  }
  /* Keyframes for the star pulse effect */
  @keyframes pulse-stars {
    0% {
      transform: scale(0.5); /* Start small */
      opacity: 0.5;
    }
    50% {
      transform: scale(1.5); /* Grow the pulse */
      opacity: 1; /* Fully visible */
    }
    100% {
      transform: scale(2.5); /* End size of the pulse */
      opacity: 0; /* Fade out */
    }
  }
`;

const StyledTip = styled.p<{ $isVisible: boolean; $isSmallTemplates?: boolean }>`
  position: absolute;
  width: 100%;
  top: 70px;
  left: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;

  h1 {
    font-size: ${({ $isSmallTemplates }) => ($isSmallTemplates ? '22px' : '36px')};
    text-align: center;
    margin: 0;
  }

  transition: opacity 1s ease-in-out;

  p {
    margin: 0 auto;
  }

  @media (max-width: 600px) or (max-height: 600px) {
    font-size: 20px;

    h1 {
      font-size: 20px !important;
    }
  }
`;
